import { toast } from "react-toastify";
import throttle from "lodash/throttle";
import type { DebouncedFunc } from "lodash";

import firestore from "src/apiService/firestore";
import { getUserId } from "src/config/storage";
import {
  userPlanSelector,
  effectiveUserTimeZoneSelector,
  isImpersonationSessionSelector,
  isSupportUserSessionSelector,
  userGetInventoryTypeSelector,
  featureFlagSelector,
} from "src/store/system/selector";
import { getSalesByTransactionId } from "src/store/sale/selector";
import {
  calculatedLoadedFromDateSelector,
  initialSalesTransactionsSyncCompleteSelector,
} from "src/store/userDataLoad/selector";
import { getDepopIntegrations } from "src/store/plaidIntegration/selector";
import type { DepopIntegration } from "src/interfaces/plaidIntegration.interface";
import type { Sale } from "src/interfaces/sale.interface";
import type { AppState } from "src/store";
import { uploadFile } from "src/utils/storage";
import type MonthYear from "src/interfaces/monthYear.interface";
import {
  endOfMonthInTimeZone,
  startOfMonthInTimeZone,
} from "src/utils/timezone";
import {
  getDepopUser,
  RequiredExtensionVersion,
} from "src/utils/extension/depop";
import {
  depopIntegrationCreate,
  depopProcessFile,
  clearDepopAttributesCache,
} from "src/apiService/modules/depop";
import { recordEventWithCheck } from "src/apiService/modules/events";
import { getJSONFileApi } from "src/apiService/modules/uploads";
import Cacher from "src/utils/Cacher";
import parallelize from "src/utils/parallelize";

import type {
  Dispatcher,
  SyncStatus,
  ErrorSyncStatusCode,
  ResumeSyncStatus,
} from "../../types";
import { updateSyncStatus as _updateSyncStatus } from "../actions";
import {
  saveSyncDate,
  getLastSyncDate,
  setBeforeUnloadHandler,
  clearBeforeUnloadHandler,
  ReSyncInterval,
  RunningKeys,
  isUserAllowedToDirectImport,
  shouldRunDailySync,
  checkIfItIsInitialImport,
  checkExtensionOrDispatchError,
  notifySyncAlreadyLocked,
} from "../utils";
import {
  withSyncLock,
  claimExpiredLease,
  DirectImportSyncLockTtlMs,
  type SyncLock,
} from "../syncLock";

import DepopClient, { NotLoggedError } from "./DepopClient";
import {
  compactDepopAttributes,
  depopAttributesStoragePath,
  isDepopAttributesFresh,
} from "./attributes";
import {
  clearDepopInventoryResume,
  clearDepopSalesResume,
  saveDepopInventoryResume,
  saveDepopSalesResume,
} from "./depopDirectImport";
import { discardDepopResume } from "./depopResumeOffer";
import {
  getExistingDepopInventoryListingIds,
  loadPendingDepopInventoryListingIds,
  writeDepopInventoryDownloadBatch,
} from "./depopInventoryDaily";
import {
  getDepopListingId,
  getDepopReceiptId,
  inventorySyncedFlagsForPage,
  salesExistFlagsForPage,
  salesSyncedFlagsForPage,
  unsyncedItemsFromFlags,
} from "./resumeCutPoint";

const ParalelizeJobs = 3;
const InventoryProgressThrottleMs = 2_000;
/** Lookback window for refunds on already-imported sales (daily sync only). */
const RefundLookbackDays = 30;

type DepopProductBySlugCache = Cacher<string, unknown>;
type DepopInventoryProgressUpdater = DebouncedFunc<(current: number) => void>;

function fetchProductBySlugEditListingCached(
  client: DepopClient,
  cache: DepopProductBySlugCache,
  slug: string
) {
  return cache.run(slug, () => client.fetchProductBySlugEditListing(slug));
}

function getReceiptId(receipt: unknown): string | null {
  return getDepopReceiptId(receipt);
}

function getReceiptDate(receipt: Record<string, unknown>): Date | null {
  const raw = receipt?.soldTimestamp;
  if (!raw) return null;
  const d = new Date(String(raw));
  return Number.isNaN(d.getTime()) ? null : d;
}

function hasListBuyerRefundAmount(receipt: Record<string, unknown>): boolean {
  return !!receipt?.buyerRefundAmount;
}

/**
 * True when a main sale exists for the purchase and no return has been booked yet.
 */
function saleNeedsRefundImport(
  sales: Array<Pick<Sale, "is_return" | "return_id">> | undefined
): boolean {
  if (!sales?.length) return false;
  const mainSales = sales.filter((sale) => !sale.is_return);
  if (!mainSales.length) return false;
  if (sales.some((sale) => sale.is_return)) return false;
  if (mainSales.some((sale) => !!sale.return_id)) return false;
  return true;
}

function canUseReduxForRefundLookback(
  state: AppState,
  lookbackFloor: Date
): boolean {
  if (!initialSalesTransactionsSyncCompleteSelector(state)) return false;
  const loadedFrom = calculatedLoadedFromDateSelector(state);
  return loadedFrom.getTime() <= lookbackFloor.getTime();
}

async function saleNeedsRefundImportForPurchaseId(
  purchaseId: string,
  {
    useRedux,
    state,
    uid,
  }: {
    useRedux: boolean;
    state: AppState;
    uid: string;
  }
): Promise<boolean> {
  if (useRedux) {
    return saleNeedsRefundImport(getSalesByTransactionId(state, purchaseId));
  }

  const snapshot = await firestore()
    .collection("Sales")
    .where("user", "==", uid)
    .where("transaction_id", "==", purchaseId)
    .get();

  return saleNeedsRefundImport(snapshot.docs.map((doc) => doc.data() as Sale));
}

function getProductListingId(product: unknown): string | null {
  return getDepopListingId(product);
}

function getProductDate(product: Record<string, unknown>): Date | null {
  const raw = product?.dateCreated || product?.dateUpdated;
  if (!raw) return null;
  const d = new Date(String(raw));
  return Number.isNaN(d.getTime()) ? null : d;
}

function getSellingCount(productCounts: unknown): number | undefined {
  const selling = (productCounts as Record<string, unknown> | undefined)
    ?.selling;
  return typeof selling === "number" && Number.isFinite(selling)
    ? selling
    : undefined;
}

/** Fetch the per-receipt detail for a page and attach it as `details`. */
async function enrichReceiptsWithDetail(
  client: DepopClient,
  receipts: Record<string, unknown>[]
) {
  await parallelize(
    receipts,
    async (receipt) => {
      const purchaseId = getReceiptId(receipt);
      if (!purchaseId) return;
      try {
        receipt.details = await client.fetchReceiptDetail(purchaseId);
      } catch (e) {
        console.warn("(depop) failed to fetch receipt detail", purchaseId, e);
      }
    },
    ParalelizeJobs
  );
  return receipts;
}

/** Fetch edit-listing product detail for each receipt item and attach as `product`. */
async function enrichReceiptItemsWithProductDetail(
  client: DepopClient,
  receipts: Record<string, unknown>[],
  cache: DepopProductBySlugCache
) {
  const items = receipts.flatMap((receipt) => {
    const details = receipt.details as Record<string, unknown> | undefined;
    const rawItems = details?.items ?? receipt.items;
    return Array.isArray(rawItems)
      ? (rawItems as Record<string, unknown>[])
      : [];
  });

  await parallelize(
    items,
    async (item) => {
      const slug = item?.slug;
      if (typeof slug !== "string" || !slug) return;
      try {
        item.product = await fetchProductBySlugEditListingCached(
          client,
          cache,
          slug
        );
      } catch (e) {
        console.warn(
          "(depop) failed to fetch product detail for sale item",
          slug,
          e
        );
      }
    },
    ParalelizeJobs
  );
  return receipts;
}

/** Fetch edit-listing product detail for a page and attach it as `details`. */
async function enrichProductsWithDetail(
  client: DepopClient,
  products: Record<string, unknown>[],
  cache: DepopProductBySlugCache
) {
  await parallelize(
    products,
    async (product) => {
      const slug = product?.slug;
      if (typeof slug !== "string" || !slug) return;
      try {
        product.details = await fetchProductBySlugEditListingCached(
          client,
          cache,
          slug
        );
      } catch (e) {
        console.warn("(depop) failed to fetch product detail", slug, e);
      }
    },
    ParalelizeJobs
  );
  return products;
}

async function updateIntegration(integrationId: string) {
  try {
    const data = await getDepopUser();

    if (data.cookies && data.userId) {
      await depopIntegrationCreate({
        integrationId,
        cookies: data.cookies,
        username: data.username,
        userId: data.userId,
      });
      recordEventWithCheck({
        type: "feature-adoption",
        feature: "integration-added",
        element: "sale-platform-depop",
      });
    }
  } catch (err) {
    console.error("There was an error:", err);
  }
}

function updateSyncStatus(status: SyncStatus) {
  return _updateSyncStatus({
    integration: "depop" as const,
    status,
  });
}

function createInventoryProgressUpdater(
  dispatch: (action: ReturnType<typeof updateSyncStatus>) => void,
  total: number | undefined
): DepopInventoryProgressUpdater | undefined {
  if (total === undefined) return undefined;

  return throttle(
    (current: number) => {
      dispatch(
        updateSyncStatus({
          type: "syncing",
          step: "direct-import-inventory",
          description: `(Inventory: ${Math.min(current, total)} / ${total})`,
        })
      );
    },
    InventoryProgressThrottleMs,
    { leading: true, trailing: true }
  );
}

async function verifyLoggedInUser(
  client: DepopClient,
  integration: DepopIntegration
) {
  const cookieUserId = await client.getUserIdFromCookie();
  if (String(cookieUserId) !== String(integration.uid)) {
    throw new NotLoggedError();
  }
}

/**
 * Refresh the per-user compacted attributes file when missing or older than a
 * week. Failures are logged and ignored so sync can continue (backend falls
 * back to the bundled attributes).
 */
async function ensureDepopAttributes(client: DepopClient) {
  const uid = await getUserId();
  const storagePath = depopAttributesStoragePath(uid);

  let needsUpdate = true;
  try {
    const existing = await getJSONFileApi(storagePath);
    needsUpdate = !isDepopAttributesFresh(existing);
  } catch (e) {
    needsUpdate = true;
  }

  if (!needsUpdate) return;

  try {
    const raw = await client.fetchAttributes();
    const compacted = compactDepopAttributes(raw);
    if (!compacted.department.length || !compacted.group.length) {
      throw new Error("compacted Depop attributes were empty");
    }
    await uploadFile(
      storagePath,
      JSON.stringify({
        ...compacted,
        updatedAt: new Date().toISOString(),
      })
    );
    clearDepopAttributesCache();
  } catch (e) {
    console.warn("(depop) failed to refresh attributes", e);
  }
}

/**
 * Rechecks in-process Depop sales. Re-fetches each receipt's detail and
 * re-uploads them (as a daily-sync sales file) so the server remaps and merges
 * the final values onto the existing in-process sale docs.
 */
async function syncInProcess(
  client: DepopClient,
  integration: DepopIntegration,
  productCache: DepopProductBySlugCache,
  lock: SyncLock
) {
  const uid = await getUserId();
  const db = firestore();
  const snapshot = await db
    .collection("Sales")
    .where("user", "==", uid)
    .where("in_process", "==", true)
    .where("import_platform", "==", "depop")
    .get();

  if (snapshot.empty) return;

  const transactionIds = Array.from(
    new Set(
      snapshot.docs
        .map((doc) => doc.data().transaction_id)
        .filter((id): id is string => !!id)
    )
  );

  const receipts: Record<string, unknown>[] = [];
  await parallelize(
    transactionIds,
    async (purchaseId) => {
      lock.throwIfAborted();
      try {
        const detail = await client.fetchReceiptDetail(purchaseId);
        if (detail) receipts.push({ ...detail, details: detail });
      } catch (e) {
        console.warn("(depop) syncInProcess detail failed", purchaseId, e);
      }
    },
    ParalelizeJobs
  );

  if (!receipts.length) return;

  await enrichReceiptItemsWithProductDetail(client, receipts, productCache);

  lock.throwIfAborted();
  const timestamp = Date.now();
  const filename = `${timestamp}_${integration.id}_inprocess.json`;
  await uploadFile(`${uid}/depopSales/${filename}`, JSON.stringify(receipts));
  await depopProcessFile(integration.id, "sales", "daily-sync", filename, {});
}

async function uploadSalesBatch(
  integration: DepopIntegration,
  receipts: unknown[],
  importType: "initial-import" | "direct-import" | "daily-sync",
  {
    uploadSalesId,
    uploadReturnsId,
    timestamp,
    batchIndex,
  }: {
    uploadSalesId?: string;
    uploadReturnsId?: string;
    timestamp: number;
    batchIndex: number;
  },
  lock: SyncLock
) {
  if (!receipts.length) return;

  lock.throwIfAborted();

  const uid = await getUserId();
  const currentFilename = `${timestamp}_${integration.id}_${String(
    batchIndex
  ).padStart(2, "0")}.json`;

  await uploadFile(
    `${uid}/depopSales/${currentFilename}`,
    JSON.stringify(receipts)
  );
  await depopProcessFile(integration.id, "sales", importType, currentFilename, {
    uploadSalesId,
    uploadReturnsId,
  });
}

async function syncSalesDaily(
  client: DepopClient,
  integration: DepopIntegration,
  date: Date,
  productCache: DepopProductBySlugCache,
  lock: SyncLock
) {
  const lastDateStr = getLastSyncDate(integration, "depopLastSyncDateSales");
  const lastDate = new Date(lastDateStr);
  const timestamp = date.valueOf();
  let batchIndex = 0;
  const seenPurchaseIds = new Set<string>();

  for await (const receipts of client.getReceiptsFull()) {
    lock.throwIfAborted();
    let shouldStop = false;
    const candidates: Record<string, unknown>[] = [];

    for (const receipt of receipts as Record<string, unknown>[]) {
      const purchaseId = getReceiptId(receipt);
      if (purchaseId) {
        if (seenPurchaseIds.has(purchaseId)) continue;
        seenPurchaseIds.add(purchaseId);
      }

      const receiptDate = getReceiptDate(receipt);
      if (receiptDate && receiptDate < lastDate) {
        shouldStop = true;
        break;
      }

      candidates.push(receipt);
    }

    // Skip enrich+upload when a Depop sale is already booked (any import).
    const existFlags = await salesExistFlagsForPage(candidates);
    const toUpload = unsyncedItemsFromFlags(candidates, existFlags);

    if (toUpload.length) {
      await enrichReceiptsWithDetail(client, toUpload);
      await enrichReceiptItemsWithProductDetail(client, toUpload, productCache);
      await uploadSalesBatch(
        integration,
        toUpload,
        "daily-sync",
        {
          timestamp,
          batchIndex: batchIndex++,
        },
        lock
      );
    }

    if (shouldStop) break;
  }
}

/**
 * After daily sync, re-list receipts in the soldTimestamp window that daily sync
 * did not cover: the 30 days **immediately before** `depopLastSyncDateSales`
 * (`[lastSync − 30d, lastSync)`), not “calendar last 30 days from now”.
 * Filter to list rows with buyerRefundAmount, keep only purchases that already
 * have a sale and no refund, then enrich + import like daily sync so the
 * backend can create returns.
 */
async function syncRefundLookback(
  client: DepopClient,
  integration: DepopIntegration,
  date: Date,
  productCache: DepopProductBySlugCache,
  lock: SyncLock,
  getState: () => AppState
) {
  const lookbackCeiling = new Date(
    getLastSyncDate(integration, "depopLastSyncDateSales")
  );
  // 30 days before last sync (end exclusive = lastSync), not now − 30 days.
  const lookbackFloor = new Date(lookbackCeiling);
  lookbackFloor.setDate(lookbackFloor.getDate() - RefundLookbackDays);

  if (!(lookbackFloor.getTime() < lookbackCeiling.getTime())) return;

  const state = getState();
  const useRedux = canUseReduxForRefundLookback(state, lookbackFloor);
  const uid = await getUserId();
  const seenPurchaseIds = new Set<string>();
  const refundCandidates: Record<string, unknown>[] = [];

  for await (const receipts of client.getReceiptsFull()) {
    let shouldStop = false;

    for (const receipt of receipts as Record<string, unknown>[]) {
      lock.throwIfAborted();

      const purchaseId = getReceiptId(receipt);
      if (purchaseId) {
        if (seenPurchaseIds.has(purchaseId)) continue;
        seenPurchaseIds.add(purchaseId);
      }

      const receiptDate = getReceiptDate(receipt);
      if (!receiptDate) continue;

      if (receiptDate < lookbackFloor) {
        shouldStop = true;
        break;
      }

      // Already covered by syncSalesDaily (soldTimestamp >= last sync cutoff).
      if (receiptDate >= lookbackCeiling) continue;

      if (!hasListBuyerRefundAmount(receipt)) continue;
      if (!purchaseId) continue;

      refundCandidates.push(receipt);
    }

    if (shouldStop) break;
  }

  if (!refundCandidates.length) return;

  const toImport: Record<string, unknown>[] = [];
  await parallelize(
    refundCandidates,
    async (receipt) => {
      lock.throwIfAborted();
      const purchaseId = getReceiptId(receipt);
      if (!purchaseId) return;

      const needsImport = await saleNeedsRefundImportForPurchaseId(purchaseId, {
        useRedux,
        state,
        uid,
      });
      if (needsImport) toImport.push(receipt);
    },
    ParalelizeJobs
  );

  if (!toImport.length) return;

  await enrichReceiptsWithDetail(client, toImport);
  await enrichReceiptItemsWithProductDetail(client, toImport, productCache);

  lock.throwIfAborted();
  const filename = `${date.valueOf()}_${integration.id}_refund_lookback.json`;
  await uploadFile(`${uid}/depopSales/${filename}`, JSON.stringify(toImport));
  await depopProcessFile(integration.id, "sales", "daily-sync", filename, {});
}

async function syncInventoryDaily(
  client: DepopClient,
  integration: DepopIntegration,
  date: Date,
  productCache: DepopProductBySlugCache,
  skipListingIds: Set<string>,
  lock: SyncLock,
  pendingInventoryEnabled: boolean
) {
  const timestamp = date.valueOf();
  const lastDateStr = getLastSyncDate(
    integration,
    "depopLastSyncDateInventory"
  );
  const lastDate = new Date(lastDateStr);
  const seenListingIds = new Set<string>();
  let batchIndex = 0;

  for await (const products of client.getProductsFull()) {
    lock.throwIfAborted();
    let shouldStop = false;
    const pageItems: Record<string, unknown>[] = [];

    for (const product of products as Record<string, unknown>[]) {
      const listingId = getProductListingId(product);
      if (listingId) {
        if (seenListingIds.has(listingId)) continue;
        seenListingIds.add(listingId);
      }

      // Daily sync stays list-only: use the list date to decide the cutoff so
      // we avoid extra edit-listing detail requests.
      const itemDate = getProductDate(product);
      if (itemDate && itemDate < lastDate) {
        shouldStop = true;
        break;
      }
      if (listingId && skipListingIds.has(listingId)) continue;
      pageItems.push(product);
    }

    if (pageItems.length) {
      await enrichProductsWithDetail(client, pageItems, productCache);
      await writeDepopInventoryDownloadBatch({
        integration,
        items: pageItems,
        date,
        timestamp,
        batchIndex: batchIndex++,
        lock,
        pendingInventoryEnabled,
      });
      for (const item of pageItems) {
        const id = getProductListingId(item);
        if (id) skipListingIds.add(id);
      }
    }

    if (shouldStop) break;
  }
}

function syncDaily(integrationId: string, inventory: boolean) {
  return async (dispatch, getState): Promise<boolean> => {
    const started = await withSyncLock(
      integrationId,
      RunningKeys,
      async (lock) => {
        const db = firestore();
        const start = new Date();
        const getIntegration = () => {
          const integration = getDepopIntegrations(getState()).find(
            ({ id }) => id === integrationId
          );
          if (!integration)
            throw Error(`Integration not found ${integrationId}`);
          return integration;
        };

        const client = new DepopClient();
        await verifyLoggedInUser(client, getIntegration());
        await updateIntegration(integrationId);
        await ensureDepopAttributes(client);
        const productCache = new Cacher<string, unknown>();

        dispatch(updateSyncStatus({ type: "syncing", step: "sales" }));
        await syncSalesDaily(
          client,
          getIntegration(),
          start,
          productCache,
          lock
        );
        await syncRefundLookback(
          client,
          getIntegration(),
          start,
          productCache,
          lock,
          getState
        );
        await syncInProcess(client, getIntegration(), productCache, lock);

        lock.throwIfAborted();
        await saveSyncDate(
          integrationId,
          "depopLastSyncDateSales",
          start.toISOString()
        );

        if (inventory) {
          dispatch(updateSyncStatus({ type: "syncing", step: "inventory" }));
          const pendingInventoryEnabled = !!featureFlagSelector(
            getState(),
            "pendingInventory"
          );
          const skipListingIds = getExistingDepopInventoryListingIds(
            getState()
          );
          const pendingIds = await loadPendingDepopInventoryListingIds({
            integrationId,
            lastSyncDate: new Date(
              getLastSyncDate(getIntegration(), "depopLastSyncDateInventory")
            ),
            pendingInventoryEnabled,
          });
          for (const id of pendingIds) skipListingIds.add(id);
          await syncInventoryDaily(
            client,
            getIntegration(),
            start,
            productCache,
            skipListingIds,
            lock,
            pendingInventoryEnabled
          );
          lock.throwIfAborted();
          await saveSyncDate(
            integrationId,
            "depopLastSyncDateInventory",
            start.toISOString()
          );
        }

        lock.throwIfAborted();
        await db.collection("Plaid_Integrations").doc(integrationId).update({
          lastSync: start.toISOString(),
        });
        await updateIntegration(integrationId);
      }
    );

    if (!started) {
      notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Depop");
    }
    return started;
  };
}

export function depopSync(
  integration: DepopIntegration,
  dispatcher?: Dispatcher
) {
  return async (dispatch, getState) => {
    const extensionId = checkExtensionOrDispatchError(
      dispatch,
      updateSyncStatus,
      "Depop",
      dispatcher,
      RequiredExtensionVersion
    );
    if (!extensionId) return;

    setBeforeUnloadHandler("Depop");

    try {
      const inventory =
        integration.inventory !== false &&
        userGetInventoryTypeSelector(getState()) !== "cash";
      const started = await dispatch(syncDaily(integration.id, inventory));
      if (started) dispatch(updateSyncStatus(undefined));
    } catch (e) {
      if (e instanceof NotLoggedError) console.warn(e);
      else console.error(e);
      let code: ErrorSyncStatusCode = "unknown";
      let message: string = (e as Error).message || (e as Error).toString();

      if (e instanceof NotLoggedError) code = "user-not-logged-in";

      dispatch(
        updateSyncStatus({
          type: "error",
          message,
          code,
          dispatcher,
        })
      );
    } finally {
      clearBeforeUnloadHandler("Depop");
    }
  };
}

export function depopSyncWithCheck(integrationId: string, noTimeCheck = false) {
  return async (dispatch, getState) => {
    const state = getState();
    // Accountant / support impersonation sessions cannot trigger client-side syncs.
    if (isImpersonationSessionSelector(state)) return;
    const plan = userPlanSelector(state);

    if (plan === "ultimate") {
      const integration = getDepopIntegrations(state)?.find(
        ({ id }) => id === integrationId
      );

      if (
        integration &&
        integration.type === "depop" &&
        integration.sync &&
        !integration.error
      ) {
        const shouldRun =
          noTimeCheck ||
          (!isSupportUserSessionSelector(state) &&
            (await shouldRunDailySync(integration.id, ReSyncInterval)));

        if (shouldRun) {
          await dispatch(
            depopSync(integration, noTimeCheck ? "manual" : "auto")
          );
        }
      }
    }
  };
}

export function depopDismissResume(
  integrationId: string,
  job: ResumeSyncStatus["job"]
) {
  return async (dispatch) => {
    try {
      await discardDepopResume(integrationId, job);
      dispatch(updateSyncStatus(undefined));
    } catch (e) {
      console.error(e);
      toast.error(`Error dismissing the resume alert: ${(e as Error).message}`);
    }
  };
}

export function depopSyncSales(
  integrationId: string,
  {
    start,
    end,
    resume = false,
  }: { start: MonthYear; end: MonthYear; resume?: boolean }
) {
  return async (
    dispatch,
    getState
  ): Promise<{ success?: boolean; error?: boolean }> => {
    const db = firestore();
    const state = getState();

    if (!isUserAllowedToDirectImport(state, "depopSales")) {
      return { error: true };
    }

    const integration = getDepopIntegrations(state)?.find(
      ({ id }) => id === integrationId
    );

    if (!integration || integration.type !== "depop" || integration.error) {
      return { error: true };
    }

    const existingResume = resume
      ? integration.depopSalesImportResume
      : undefined;
    if (resume && !existingResume) {
      return { error: true };
    }

    const salesStart = existingResume?.start ?? start;
    const salesEnd = existingResume?.end ?? end;

    const extensionId = checkExtensionOrDispatchError(
      dispatch,
      updateSyncStatus,
      "Depop",
      "manual",
      RequiredExtensionVersion
    );
    if (!extensionId) return { error: true };

    const client = new DepopClient();
    try {
      await verifyLoggedInUser(client, integration);
    } catch (e) {
      console.warn(e);
      dispatch(
        updateSyncStatus({
          type: "error",
          message: "You must be logged in to Depop with the correct user",
          code: "user-not-logged-in",
          dispatcher: "manual",
        })
      );
      return { error: true };
    }
    await updateIntegration(integrationId);

    const runningKeys = ["salesIsRunning" as const];
    const isInitialImport = checkIfItIsInitialImport(state, "depopSales");
    const importType =
      existingResume?.importType ??
      (isInitialImport ? "initial-import" : "direct-import");
    setBeforeUnloadHandler("Depop");
    let lockBlocked = false;

    let claimedOwner: string | undefined;
    if (resume) {
      claimedOwner =
        (await claimExpiredLease(integrationId, runningKeys, {
          ttlMs: DirectImportSyncLockTtlMs,
        })) ?? undefined;
      if (!claimedOwner) {
        lockBlocked = true;
        notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Depop");
        return { error: true };
      }
    }

    try {
      const started = await withSyncLock(
        integrationId,
        runningKeys,
        async (lock) => {
          await ensureDepopAttributes(client);
          dispatch(
            updateSyncStatus({ type: "syncing", step: "direct-import-sales" })
          );

          const uid = await getUserId();
          let timestamp = existingResume?.timestamp ?? Date.now();
          let uploadSalesId = existingResume?.uploadSalesId;
          let uploadReturnsId = existingResume?.uploadReturnsId;
          let batchIndex = existingResume?.batchIndex ?? 0;

          if (!uploadSalesId || !uploadReturnsId) {
            timestamp = Date.now();
            const upload = db.collection("Uploads").doc();
            await upload.set({
              user: uid,
              type: "sales",
              provider: "depop",
              filename: `${uid}/depopSales/${timestamp}_${integrationId}.json`,
              n: 0,
              timestamp,
              id: upload.id,
            });
            const uploadReturns = db.collection("Uploads").doc();
            await uploadReturns.set({
              user: uid,
              type: "returns",
              provider: "depop",
              filename: `${uid}/depopSales/${timestamp}_${integrationId}.json`,
              n: 0,
              timestamp,
              id: uploadReturns.id,
            });
            uploadSalesId = upload.id;
            uploadReturnsId = uploadReturns.id;
            await saveDepopSalesResume(integrationId, {
              batchIndex: 0,
              timestamp,
              importType,
              uploadSalesId,
              uploadReturnsId,
              start: salesStart,
              end: salesEnd,
            });
          }

          const tz = effectiveUserTimeZoneSelector(getState());
          const startDate = startOfMonthInTimeZone(
            salesStart.year,
            salesStart.month,
            tz
          );
          const endDate = endOfMonthInTimeZone(
            salesEnd.year,
            salesEnd.month,
            tz
          );
          const seenPurchaseIds = new Set<string>();
          const productCache = new Cacher<string, unknown>();
          // Resume always restarts list paging from the start (opaque cursors
          // are not durable) and skips rows already under this session's Uploads.
          const skipSynced = !!existingResume;

          for await (const receipts of client.getReceiptsFull()) {
            lock.throwIfAborted();
            let shouldStop = false;
            const filtered = (receipts as Record<string, unknown>[]).filter(
              (receipt) => {
                const purchaseId = getReceiptId(receipt);
                if (!purchaseId) return true;
                if (seenPurchaseIds.has(purchaseId)) return false;
                seenPurchaseIds.add(purchaseId);

                const receiptDate = getReceiptDate(receipt);
                if (!receiptDate) return true;
                if (receiptDate < startDate) {
                  shouldStop = true;
                  return false;
                }
                return receiptDate >= startDate && receiptDate <= endDate;
              }
            );

            let toProcess = filtered;
            if (skipSynced && filtered.length) {
              const flags = await salesSyncedFlagsForPage(
                filtered,
                uploadSalesId,
                uploadReturnsId
              );
              toProcess = unsyncedItemsFromFlags(filtered, flags);
            }

            if (toProcess.length) {
              await enrichReceiptsWithDetail(client, toProcess);
              await enrichReceiptItemsWithProductDetail(
                client,
                toProcess,
                productCache
              );
              await uploadSalesBatch(
                integration,
                toProcess,
                importType,
                {
                  uploadSalesId,
                  uploadReturnsId,
                  timestamp,
                  batchIndex: batchIndex++,
                },
                lock
              );
            }

            await saveDepopSalesResume(integrationId, {
              batchIndex,
              timestamp,
              importType,
              uploadSalesId,
              uploadReturnsId,
              start: salesStart,
              end: salesEnd,
            });

            if (shouldStop) break;
          }

          await syncInProcess(client, integration, productCache, lock);
          await clearDepopSalesResume(integrationId);
        },
        { ttlMs: DirectImportSyncLockTtlMs, claimedOwner }
      );
      if (!started) {
        lockBlocked = true;
        notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Depop");
        return { error: true };
      }
    } catch (e) {
      if (e instanceof NotLoggedError) console.warn(e);
      else console.error(e);
      toast.error(`Error: ${(e as Error).message}`);
      return { error: true };
    } finally {
      if (!lockBlocked) dispatch(updateSyncStatus(undefined));
      clearBeforeUnloadHandler("Depop");
    }

    await updateIntegration(integrationId);
    return { success: true };
  };
}

export function depopSyncInventory(
  integrationId: string,
  { resume = false }: { resume?: boolean } = {}
) {
  return async (
    dispatch,
    getState
  ): Promise<{ error?: true; success?: true }> => {
    const db = firestore();
    const state = getState();

    if (!isUserAllowedToDirectImport(state, "depopInventory")) {
      return { error: true };
    }
    if (userGetInventoryTypeSelector(getState()) === "cash") {
      return { error: true };
    }

    const integration = getDepopIntegrations(state)?.find(
      ({ id }) => id === integrationId
    );

    if (!integration || integration.type !== "depop" || integration.error) {
      return { error: true };
    }

    const existingResume = resume
      ? integration.depopInventoryImportResume
      : undefined;
    if (resume && !existingResume) {
      return { error: true };
    }

    const extensionId = checkExtensionOrDispatchError(
      dispatch,
      updateSyncStatus,
      "Depop",
      "manual",
      RequiredExtensionVersion
    );
    if (!extensionId) return { error: true };

    const client = new DepopClient();
    try {
      await verifyLoggedInUser(client, integration);
    } catch (e) {
      console.warn(e);
      dispatch(
        updateSyncStatus({
          type: "error",
          message: "You must be logged in to Depop with the correct user",
          code: "user-not-logged-in",
          dispatcher: "manual",
        })
      );
      return { error: true };
    }
    await updateIntegration(integrationId);

    const runningKeys = ["inventoryIsRunning" as const];
    const isInitialImport = checkIfItIsInitialImport(state, "depopInventory");
    const importType =
      existingResume?.importType ??
      (isInitialImport ? "initial-import" : "direct-import");
    let updateInventoryProgress: DepopInventoryProgressUpdater | undefined;
    setBeforeUnloadHandler("Depop");
    let lockBlocked = false;

    let claimedOwner: string | undefined;
    if (resume) {
      claimedOwner =
        (await claimExpiredLease(integrationId, runningKeys, {
          ttlMs: DirectImportSyncLockTtlMs,
        })) ?? undefined;
      if (!claimedOwner) {
        lockBlocked = true;
        notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Depop");
        return { error: true };
      }
    }

    try {
      const started = await withSyncLock(
        integrationId,
        runningKeys,
        async (lock) => {
          await ensureDepopAttributes(client);
          dispatch(
            updateSyncStatus({
              type: "syncing",
              step: "direct-import-inventory",
            })
          );

          let sellingCount: number | undefined;
          try {
            sellingCount = getSellingCount(await client.getProductCounts());
          } catch (e) {
            console.warn("(depop) failed to fetch product counts", e);
          }
          updateInventoryProgress = createInventoryProgressUpdater(
            dispatch,
            sellingCount
          );
          updateInventoryProgress?.(0);

          const uid = await getUserId();
          let timestamp = existingResume?.timestamp ?? Date.now();
          let uploadInventoryId = existingResume?.uploadInventoryId;
          let batchIndex = existingResume?.batchIndex ?? 0;

          if (!uploadInventoryId) {
            timestamp = Date.now();
            const upload = db.collection("Uploads").doc();
            await upload.set({
              user: uid,
              type: "inventory",
              provider: "depop",
              filename: `${uid}/depopInventory/${timestamp}_${integrationId}.json`,
              n: 0,
              timestamp,
              id: upload.id,
            });
            uploadInventoryId = upload.id;
            await saveDepopInventoryResume(integrationId, {
              batchIndex: 0,
              timestamp,
              importType,
              uploadInventoryId,
            });
          }

          const seenListingIds = new Set<string>();
          const existingListingIds = getExistingDepopInventoryListingIds(
            getState()
          );
          let progressCurrent = 0;
          const productCache = new Cacher<string, unknown>();
          const skipSynced = !!existingResume;

          for await (const products of client.getProductsFull()) {
            lock.throwIfAborted();
            const pageItems: Record<string, unknown>[] = [];

            for (const item of products as Record<string, unknown>[]) {
              const listingId = getProductListingId(item);
              if (!listingId) {
                progressCurrent++;
                pageItems.push(item);
                continue;
              }
              if (seenListingIds.has(listingId)) continue;
              seenListingIds.add(listingId);
              progressCurrent++;
              if (existingListingIds.has(listingId)) continue;
              pageItems.push(item);
            }

            updateInventoryProgress?.(progressCurrent);

            let toProcess = pageItems;
            if (skipSynced && pageItems.length) {
              const flags = await inventorySyncedFlagsForPage(
                pageItems,
                uploadInventoryId
              );
              toProcess = unsyncedItemsFromFlags(pageItems, flags);
            }

            if (toProcess.length) {
              await enrichProductsWithDetail(client, toProcess, productCache);

              const currentFilename = `${timestamp}_${integrationId}_${String(
                batchIndex
              ).padStart(2, "0")}.json`;
              lock.throwIfAborted();
              await uploadFile(
                `${uid}/depopInventory/${currentFilename}`,
                JSON.stringify(toProcess)
              );
              await depopProcessFile(
                integrationId,
                "inventory",
                importType,
                currentFilename,
                { uploadInventoryId }
              );
              batchIndex++;
            }

            await saveDepopInventoryResume(integrationId, {
              batchIndex,
              timestamp,
              importType,
              uploadInventoryId,
            });
          }

          await clearDepopInventoryResume(integrationId);
        },
        { ttlMs: DirectImportSyncLockTtlMs, claimedOwner }
      );
      if (!started) {
        lockBlocked = true;
        notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Depop");
        return { error: true };
      }
    } catch (e) {
      if (e instanceof NotLoggedError) console.warn(e);
      else console.error(e);
      toast.error(`Error: ${(e as Error).message}`);
      return { error: true };
    } finally {
      updateInventoryProgress?.cancel();
      if (!lockBlocked) dispatch(updateSyncStatus(undefined));
      clearBeforeUnloadHandler("Depop");
    }

    await updateIntegration(integrationId);
    return { success: true };
  };
}
